import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '@/App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Users,
  AlertTriangle,
  Settings,
  LogOut,
  BarChart3,
  UserPlus,
  FileText,
  Upload,
  GraduationCap,
  UserCog,
  CheckCircle2,
  X,
  TrendingUp,
  TrendingDown,
  Shield,
  Activity,
  Clock,
  Bell,
  Search,
  Sun,
  Moon,
  Menu,
  Wifi,
  Monitor,
  Globe,
  BookOpen,
  Heart,
  MessageSquare,
  Brain,
  Mail,
  ClipboardList,
  Layers,
  ArrowRight,
  ChevronRight,
} from 'lucide-react';
import { supabase } from '@/config/supabase';
import { dashboardService, type SchoolAdminStats } from '@/services/dashboardService';
import { IncidentManagement } from './IncidentManagement';
import { UserManagement } from './UserManagement';
import { AnalyticsDashboard } from './AnalyticsDashboard';
import { SchoolSettings } from './SchoolSettings';
import { SchoolAdminProfile } from './SchoolAdminProfile';
import { SchoolAdminNotifications } from './SchoolAdminNotifications';
import { LiveActivityFeed } from './LiveActivityFeed';
import { LiveScreensView } from './LiveScreensView';
import { WebTrafficDashboard } from './WebTrafficDashboard';
import { ClassroomView } from './ClassroomView';
import { WellnessDashboard } from './WellnessDashboard';
import { MessageSafetyReport } from './MessageSafetyReport';
import { AIDetectionPanel } from './AIDetectionPanel';
import { InviteTracker } from './InviteTracker';
import { AuditLogView } from './AuditLogView';
import { WorkloadView } from './WorkloadView';
import { SafetyScoreBreakdown } from './SafetyScoreBreakdown';
import { Documentation } from './Documentation';
import { API_URL } from '@/services/apiClient';

type SchoolAdminPage =
  | 'dashboard'
  | 'adduser'
  | 'users'
  | 'incidents'
  | 'reports'
  | 'notifications'
  | 'profile'
  | 'settings'
  | 'live'
  | 'screens'
  | 'traffic'
  | 'classrooms'
  | 'wellness'
  | 'messages'
  | 'ai-detection'
  | 'invites'
  | 'audit'
  | 'workload'
  | 'safety-score'
  | 'documentation';

// const PAGE_TITLES: Record<SchoolAdminPage, string> = {
//   dashboard: 'Dashboard',
//   adduser: 'Add User',
//   users: 'User Management',
//   incidents: 'Incident Management',
//   reports: 'Reports & Analytics',
//   notifications: 'Notifications',
//   profile: 'My Profile',
//   settings: 'School Settings',
//   live: 'Live Activity',
//   screens: 'Live Screens',
//   traffic: 'Web Traffic',
//   classrooms: 'Classrooms',
//   wellness: 'Digital Wellness',
//   messages: 'Message Safety',
//   'ai-detection': 'AI Detection',
//   invites: 'Invite Tracker',
//   audit: 'Audit Log',
//   workload: 'Teacher Workload',
//   'safety-score': 'Safety Score',
//   documentation: 'Documentation',
// };

interface AddUserFormData {
  email: string;
  name: string;
  role: 'student' | 'teacher';
  schoolId: string;
}

interface RecentAlert {
  id: string;
  alertType: string;
  severity: string;
  studentName: string;
  createdAt: string;
  status: string;
}

interface AnalyticsData {
  overview: {
    totalStudents: number;
    totalTeachers: number;
    totalIncidents: number;
    activeAlerts: number;
    incidentTrend: number;
    alertTrend: number;
  };
  incidentsByType: Array<{ type: string; count: number }>;
  incidentsBySeverity: Array<{ severity: string; count: number }>;
  incidentsByMonth: Array<{ month: string; count: number }>;
  topStudents: Array<{ name: string; incidentCount: number }>;
  teacherActivity: Array<{ name: string; resolvedCount: number }>;
}

// ─── Add Single User Form ────────────────────────────────────────────────────

function AddUserForm({ dm }: { dm: boolean }) {
  const { user } = useApp();
  const [formData, setFormData] = useState<AddUserFormData>({
    email: '',
    name: '',
    role: 'student',
    schoolId: user?.schoolId || '',
  });

  useEffect(() => {
    if (user?.schoolId) {
      setFormData(prev => ({ ...prev, schoolId: user.schoolId ?? '' }));
    } else {
      if (import.meta.env.DEV) console.warn('No schoolId found for school admin user');
    }
  }, [user?.schoolId]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [createdUser, setCreatedUser] = useState<{ email: string; message: string } | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setLoading(true);

    const schoolId = formData.schoolId || user?.schoolId || '';
    if (!formData.email || !formData.name) {
      setError('Email and name are required');
      setLoading(false);
      return;
    }
    if (!schoolId) {
      setError('School ID not found. Please refresh and try again.');
      setLoading(false);
      return;
    }
    if (!/.+@.+\..+/.test(formData.email)) {
      setError('Please enter a valid email address');
      setLoading(false);
      return;
    }

    try {
      const authToken = sessionStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/schools/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken && { 'Authorization': `Bearer ${authToken}` }),
        },
        body: JSON.stringify({ ...formData, schoolId }),
      });
      const result = await response.json();
      if (!response.ok) {
        const errMsg = typeof result.error === 'string'
          ? result.error
          : (result.message || JSON.stringify(result.error) || 'Failed to create user');
        throw new Error(errMsg);
      }

      setSuccess(true);
      setCreatedUser({ email: formData.email, message: result.message });
      setFormData({ email: '', name: '', role: 'student', schoolId: user?.schoolId || '' });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const cardCls = `rounded-xl border ${dm ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`;
  const titleCls = dm ? 'text-slate-100' : 'text-slate-800';
  const descCls = dm ? 'text-slate-400' : 'text-slate-500';
  const labelCls = `text-xs font-medium ${dm ? 'text-slate-300' : ''}`;
  const inputCls = dm ? 'bg-slate-700 border-slate-600 text-slate-200 placeholder:text-slate-500' : '';
  const divider = `border-t ${dm ? 'border-slate-700' : 'border-slate-100'}`;

  return (
    <div className={`${cardCls} p-6`}>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className={`text-base font-semibold flex items-center gap-2 ${titleCls}`}>
            <UserPlus className={`h-5 w-5 ${dm ? 'text-slate-400' : 'text-slate-600'}`} />
            Invite Single User
          </h3>
          <p className={`text-sm mt-0.5 ${descCls}`}>Send an invitation to a new teacher or student</p>
        </div>
        {success && createdUser && (
          <span className="text-xs px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 flex items-center gap-1.5">
            <CheckCircle2 className="h-3.5 w-3.5" />
            Invitation sent
          </span>
        )}
      </div>

      {success && createdUser && (
        <div className="mb-5 p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-start gap-3">
          <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-medium text-emerald-900">Invitation sent to {createdUser.email}</p>
            <p className="text-xs text-emerald-700 mt-0.5">{createdUser.message}</p>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-5 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className={divider} />

        <div>
          <p className={`text-xs font-semibold uppercase tracking-wider mb-3 ${dm ? 'text-slate-500' : 'text-slate-400'}`}>School</p>
          <div className="space-y-1.5">
            <Label htmlFor="schoolId" className={labelCls}>School ID</Label>
            <Input
              id="schoolId" name="schoolId"
              value={formData.schoolId || user?.schoolId || ''}
              readOnly
              className={dm ? 'bg-slate-700 border-slate-600 text-slate-300' : 'bg-gray-50'}
              style={{ borderRadius: '50px' }}
            />
            <p className={`text-xs ${dm ? 'text-slate-500' : 'text-slate-400'}`}>Automatically filled from your account</p>
          </div>
        </div>

        <div className={divider} />

        <div>
          <p className={`text-xs font-semibold uppercase tracking-wider mb-3 ${dm ? 'text-slate-500' : 'text-slate-400'}`}>User Details</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="role" className={labelCls}>User Role *</Label>
              <Select value={formData.role} onValueChange={(v) => setFormData(p => ({ ...p, role: v as 'student' | 'teacher' }))}>
                <SelectTrigger className={`btn-glass btn-glass-pill ${dm ? 'text-slate-200' : ''}`}>
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="student">Student</SelectItem>
                  <SelectItem value="teacher">Teacher</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="name" className={labelCls}>Full Name *</Label>
              <Input id="name" name="name" value={formData.name} onChange={handleInputChange} placeholder="e.g., John Doe" required className={inputCls} style={{ borderRadius: '50px' }}/>
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label htmlFor="email" className={labelCls}>Email *</Label>
              <Input id="email" name="email" type="email" value={formData.email} onChange={handleInputChange} placeholder="e.g., john.doe@example.com" required className={inputCls} style={{ borderRadius: '50px' }}/>
            </div>
          </div>
        </div>

        <div className={divider} />

        <div className="flex gap-3" data-dm={dm}>
          <button type="submit" disabled={loading} className="btn-glass btn-glass-pill btn-glass-blue flex items-center gap-2">
            {loading
              ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />Sending...</>
              : <><UserPlus className="h-4 w-4" />Send Invitation</>}
          </button>
          <button type="button" className="btn-glass btn-glass-pill"
            onClick={() => { setFormData({ email: '', name: '', role: 'student', schoolId: user?.schoolId || '' }); setError(''); setSuccess(false); setCreatedUser(null); }}>
            Clear
          </button>
        </div>
      </form>
    </div>
  );
}

// ─── Bulk Upload ─────────────────────────────────────────────────────────────

function BulkUploadUsers({ dm }: { dm: boolean }) {
  const { user } = useApp();
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [uploadResults, setUploadResults] = useState<{ successful: number; failed: number; errors: string[] } | null>(null);

  const generateTemporaryPassword = (): string => {
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    const secureRandom = (max: number) => { const a = new Uint32Array(1); crypto.getRandomValues(a); return a[0] % max; };
    let password = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[secureRandom(26)];
    password += 'abcdefghijklmnopqrstuvwxyz'[secureRandom(26)];
    password += '0123456789'[secureRandom(10)];
    password += '!@#$%^&*'[secureRandom(8)];
    for (let i = password.length; i < 12; i++) password += charset[secureRandom(charset.length)];
    const arr = password.split('');
    for (let i = arr.length - 1; i > 0; i--) { const j = secureRandom(i + 1); [arr[i], arr[j]] = [arr[j], arr[i]]; }
    return arr.join('');
  };

  const parseCSV = (text: string) => {
    const lines = text.split('\n').filter(l => l.trim());
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    const nameIdx = headers.findIndex(h => ['name', 'fullname', 'full_name'].includes(h));
    const emailIdx = headers.findIndex(h => h === 'email');
    const roleIdx = headers.findIndex(h => h === 'role');
    if (nameIdx === -1 || emailIdx === -1 || roleIdx === -1) throw new Error('CSV must have headers: name, email, role');
    return lines.slice(1).map(line => {
      const v = line.split(',').map(x => x.trim());
      return { name: v[nameIdx], email: v[emailIdx], role: v[roleIdx] };
    });
  };

  const handleUpload = async () => {
    if (!file) { setError('Please select a CSV file'); return; }
    setLoading(true); setError(''); setSuccess(false); setUploadResults(null);
    try {
      const users = parseCSV(await file.text());
      let successful = 0, failed = 0;
      const errors: string[] = [];
      if (!supabase) { setUploadResults({ successful: 0, failed: users.length, errors: ['Supabase is not configured'] }); return; }
      for (const u of users) {
        try {
          if (!/.+@.+\..+/.test(u.email)) { errors.push(`${u.email}: Invalid email format`); failed++; continue; }
          if (!['student', 'teacher'].includes(u.role)) { errors.push(`${u.email}: Role must be 'student' or 'teacher'`); failed++; continue; }
          const pw = generateTemporaryPassword();
          const { data: authData, error: authError } = await supabase.auth.signUp({ email: u.email, password: pw, options: { data: { full_name: u.name, role: u.role, schoolId: user?.schoolId } } });
          if (authError) throw authError;
          if (authData.user) {
            const { error: userError } = await supabase.from('User').insert({ authUid: authData.user.id, email: u.email, name: u.name, role: u.role, schoolId: user?.schoolId, hasCompletedOnboarding: false, notificationPermissions: false, pushNotificationsEnabled: false });
            if (userError) throw userError;
          }
          successful++;
        } catch (err) { failed++; errors.push(`${u.email}: ${err instanceof Error ? err.message : 'Unknown error'}`); }
      }
      setUploadResults({ successful, failed, errors });
      setSuccess(true);
      setFile(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to process CSV file');
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = () => {
    const blob = new Blob(['name,email,role\nJohn Doe,john.doe@example.com,student\nJane Smith,jane.smith@example.com,teacher'], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = Object.assign(document.createElement('a'), { href: url, download: 'user_upload_template.csv' });
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  const cardCls = `rounded-xl border ${dm ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`;
  const titleCls = dm ? 'text-slate-100' : 'text-slate-800';
  const descCls = dm ? 'text-slate-400' : 'text-slate-500';
  const divider = `border-t ${dm ? 'border-slate-700' : 'border-slate-100'}`;

  return (
    <div className={`${cardCls} p-6`}>
      <div className="mb-5">
        <h3 className={`text-base font-semibold flex items-center gap-2 ${titleCls}`}>
          <Upload className={`h-5 w-5 ${dm ? 'text-slate-400' : 'text-slate-600'}`} />
          Bulk Upload Users
        </h3>
        <p className={`text-sm mt-0.5 ${descCls}`}>Upload multiple users via CSV file</p>
      </div>

      {success && uploadResults && (
        <div className="mb-5 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm font-medium text-blue-900 mb-1">Upload Complete</p>
          <p className="text-xs text-blue-800">Successful: {uploadResults.successful} · Failed: {uploadResults.failed}</p>
          {uploadResults.errors.length > 0 && (
            <ul className="mt-2 list-disc list-inside text-xs text-blue-700 space-y-0.5">
              {uploadResults.errors.slice(0, 5).map((e, i) => <li key={i}>{e}</li>)}
              {uploadResults.errors.length > 5 && <li>...and {uploadResults.errors.length - 5} more</li>}
            </ul>
          )}
        </div>
      )}

      {error && (
        <div className="mb-5 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      <div className="space-y-6">
        <div className={divider} />

        <div>
          <p className={`text-xs font-semibold uppercase tracking-wider mb-3 ${dm ? 'text-slate-500' : 'text-slate-400'}`}>CSV File Format</p>
          <p className={`text-sm mb-3 ${descCls}`}>
            Required columns: <code className={`text-xs px-1 py-0.5 rounded ${dm ? 'bg-slate-700 text-blue-300' : 'bg-slate-100 text-slate-700'}`}>name</code>, <code className={`text-xs px-1 py-0.5 rounded ${dm ? 'bg-slate-700 text-blue-300' : 'bg-slate-100 text-slate-700'}`}>email</code>, <code className={`text-xs px-1 py-0.5 rounded ${dm ? 'bg-slate-700 text-blue-300' : 'bg-slate-100 text-slate-700'}`}>role</code>
          </p>
          <button type="button" className="btn-glass btn-glass-pill text-sm" onClick={downloadTemplate}>Download Template</button>
        </div>

        <div className={divider} />

        <div>
          <p className={`text-xs font-semibold uppercase tracking-wider mb-3 ${dm ? 'text-slate-500' : 'text-slate-400'}`}>Upload</p>
          {/* Hidden native input — triggered by the custom area below */}
          <input
            id="csvFile" type="file" accept=".csv"
            className="sr-only"
            onChange={e => { if (e.target.files?.[0]) { setFile(e.target.files[0]); setError(''); } }}
          />
          <label
            htmlFor="csvFile"
            className={`flex items-center gap-3 px-4 py-3 rounded-lg border border-dashed cursor-pointer transition-colors ${
              file
                ? (dm ? 'border-emerald-600 bg-emerald-900/20' : 'border-emerald-400 bg-emerald-50')
                : (dm ? 'border-slate-600 bg-slate-700/40 hover:border-slate-400 hover:bg-slate-700' : 'border-slate-300 bg-slate-50 hover:border-slate-400 hover:bg-slate-100')
                
            }`}
            style={{ borderRadius: '50px' }}
          >
            <Upload className={`h-4 w-4 shrink-0 ${file ? 'text-emerald-500' : (dm ? 'text-slate-400' : 'text-slate-500')}`} />
            {file ? (
              <span className={`text-sm font-medium truncate ${dm ? 'text-emerald-400' : 'text-emerald-700'}`}>{file.name}</span>
            ) : (
              <span className={`text-sm ${dm ? 'text-slate-400' : 'text-slate-500'}`}>Click to browse for a CSV file…</span>
            )}
          </label>
        </div>

        <div className={divider} />

        <div className="flex gap-3" data-dm={dm}>
          <button onClick={handleUpload} disabled={!file || loading} className="btn-glass btn-glass-pill btn-glass-blue flex items-center gap-2">
            {loading
              ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />Uploading...</>
              : <><Upload className="h-4 w-4" />Upload Users</>}
          </button>
          {file && (
            <button type="button" className="btn-glass btn-glass-pill flex items-center gap-2"
              onClick={() => { setFile(null); setError(''); setSuccess(false); setUploadResults(null); }}>
              <X className="h-4 w-4" />Clear
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Visualization helpers ────────────────────────────────────────────────────

const TYPE_COLORS: Record<string, string> = {
  cyberbullying: 'bg-purple-500',
  self_harm: 'bg-red-500',
  violence: 'bg-orange-500',
  threat: 'bg-amber-500',
  profanity: 'bg-blue-500',
  hate_speech: 'bg-pink-500',
  none: 'bg-slate-400',
};

const SEVERITY_COLORS: Record<string, string> = {
  critical: 'bg-red-500',
  high: 'bg-orange-500',
  medium: 'bg-amber-400',
  low: 'bg-blue-400',
};

const SEVERITY_TEXT: Record<string, string> = {
  critical: 'text-red-500',
  high: 'text-orange-500',
  medium: 'text-amber-500',
  low: 'text-blue-400',
};

function HorizontalBar({ label, count, max, colorClass, dm }: { label: string; count: number; max: number; colorClass: string; dm: boolean }) {
  const pct = max > 0 ? Math.round((count / max) * 100) : 0;
  return (
    <div className="flex items-center gap-3">
      <span className={`text-xs w-28 shrink-0 capitalize truncate ${dm ? 'text-slate-400' : 'text-slate-600'}`}>{label.replace(/_/g, ' ')}</span>
      <div className={`flex-1 h-2.5 rounded-full overflow-hidden ${dm ? 'bg-slate-700' : 'bg-slate-100'}`}>
        <div className={`h-full rounded-full transition-all duration-500 ${colorClass}`} style={{ width: `${pct}%` }} />
      </div>
      <span className={`text-xs font-semibold w-6 text-right shrink-0 ${dm ? 'text-slate-300' : 'text-slate-700'}`}>{count}</span>
    </div>
  );
}

function LineTrendChart({ data, dm }: { data: Array<{ month: string; count: number }>; dm: boolean }) {
  const W = 440;
  const padX = 28, padTop = 26, padBottom = 22;
  const innerH = 96;
  const innerW = W - padX * 2;
  const totalH = padTop + innerH + padBottom;
  const maxVal = Math.max(...data.map(d => d.count), 1);
  const n = data.length;

  const pts = data.map((d, i) => ({
    x: padX + (n > 1 ? (i / (n - 1)) * innerW : innerW / 2),
    y: padTop + innerH - (d.count / maxVal) * innerH,
    ...d,
  }));

  const polyline = pts.map(p => `${p.x},${p.y}`).join(' ');
  const baseY = padTop + innerH;
  const area = [
    `M ${pts[0].x},${pts[0].y}`,
    ...pts.slice(1).map(p => `L ${p.x},${p.y}`),
    `L ${pts[pts.length - 1].x},${baseY}`,
    `L ${pts[0].x},${baseY}`,
    'Z',
  ].join(' ');

  const gridColor   = dm ? '#334155' : '#e2e8f0';
  const labelColor  = dm ? '#94a3b8' : '#64748b';
  const lineColor   = '#6366f1';
  const dotBg       = dm ? '#1e293b' : '#ffffff';
  const gradId      = 'lineTrendGrad';

  return (
    <svg viewBox={`0 0 ${W} ${totalH}`} className="w-full" style={{ height: '150px' }} preserveAspectRatio="xMidYMid meet">
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor={lineColor} stopOpacity="0.22" />
          <stop offset="100%" stopColor={lineColor} stopOpacity="0.02" />
        </linearGradient>
      </defs>

      {/* horizontal grid lines — 4 evenly spaced */}
      {[0, 0.33, 0.66, 1].map(t => (
        <line
          key={t}
          x1={padX}       y1={padTop + innerH * (1 - t)}
          x2={W - padX}   y2={padTop + innerH * (1 - t)}
          stroke={gridColor} strokeWidth="1" strokeDasharray={t === 0 ? '0' : '3 3'}
        />
      ))}

      {/* area fill */}
      <path d={area} fill={`url(#${gradId})`} />

      {/* line */}
      <polyline
        points={polyline}
        fill="none"
        stroke={lineColor}
        strokeWidth="2.5"
        strokeLinejoin="round"
        strokeLinecap="round"
      />

      {/* dots + value labels */}
      {pts.map(p => (
        <g key={p.month}>
          {/* value label above dot */}
          <text
            x={p.x} y={p.y - 9}
            textAnchor="middle" fontSize="10"
            fill={labelColor} fontWeight="600"
          >
            {p.count}
          </text>
          {/* outer ring */}
          <circle cx={p.x} cy={p.y} r="5" fill={lineColor} opacity="0.18" />
          {/* dot */}
          <circle cx={p.x} cy={p.y} r="3.5" fill={lineColor} stroke={dotBg} strokeWidth="2" />
        </g>
      ))}

      {/* month labels */}
      {pts.map(p => (
        <text
          key={`lbl-${p.month}`}
          x={p.x} y={totalH - 4}
          textAnchor="middle" fontSize="10"
          fill={labelColor}
        >
          {p.month}
        </text>
      ))}
    </svg>
  );
}

// ─── Main Dashboard ───────────────────────────────────────────────────────────

function SchoolAdminDashboard() {
  const { user, logout } = useApp();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState<SchoolAdminPage>('dashboard');
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('schoolAdminDarkMode') === 'true');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [now, setNow] = useState(new Date());
  const [stats, setStats] = useState<SchoolAdminStats | null>(null);
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [recentAlerts, setRecentAlerts] = useState<RecentAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [schoolName, setSchoolName] = useState<string | null>(null);
  const [schoolPlan, setSchoolPlan] = useState<string | null>(null);
  const [incidentStudentFilter, setIncidentStudentFilter] = useState('');

  const dm = darkMode;

  // Navigate to a page, clearing transient filters if leaving incidents
  const navigateTo = (page: SchoolAdminPage) => {
    if (page !== 'incidents') setIncidentStudentFilter('');
    setCurrentPage(page);
  };

  useEffect(() => {
    localStorage.setItem('schoolAdminDarkMode', String(darkMode));
  }, [darkMode]);

  // Live clock
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // Fetch school name + plan once
  useEffect(() => {
    const token = sessionStorage.getItem('authToken');
    if (!token) return;
    fetch(`${API_URL}/dashboard/school-admin/settings`, {
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d?.school) {
          setSchoolName(d.school.name || null);
          setSchoolPlan(d.school.subscriptionTier || 'Free');
        }
      })
      .catch(() => {});
  }, []);

  const fetchDashboard = useCallback(async () => {
    try {
      const token = sessionStorage.getItem('authToken');
      const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

      const [statsData, analyticsRes, incidentsRes] = await Promise.all([
        dashboardService.getSchoolAdminStats(),
        fetch(`${API_URL}/dashboard/school-admin/analytics?days=30`, { headers }),
        fetch(`${API_URL}/dashboard/school-admin/incidents`, { headers }),
      ]);

      setStats(statsData);

      if (analyticsRes.ok) setAnalytics(await analyticsRes.json());
      if (incidentsRes.ok) {
        const d = await incidentsRes.json();
        setRecentAlerts((d.incidents || []).slice(0, 8));
      }
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Dashboard fetch failed:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
    const interval = setInterval(fetchDashboard, 30_000);
    return () => clearInterval(interval);
  }, [fetchDashboard]);

  const handleLogout = () => {
    try { navigate('/', { replace: true }); } catch { window.location.assign('/login'); }
    Promise.resolve().then(() => logout()).catch(console.error);
  };

  const maxTypeCount = Math.max(...(analytics?.incidentsByType.map(x => x.count) ?? [1]), 1);
  const maxSeverityCount = Math.max(...(analytics?.incidentsBySeverity.map(x => x.count) ?? [1]), 1);

  // Shared card class helper
  const cardCls = `rounded-xl border ${dm ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`;
  const titleCls = dm ? 'text-white' : 'text-slate-800';
  const descCls = dm ? 'text-white' : 'text-slate-500';
  const bodyTextCls = dm ? 'text-white' : 'text-slate-700';
  const mutedCls = dm ? 'text-white' : 'text-slate-400';
  const dividerCls = dm ? 'divide-slate-700' : 'divide-slate-100';
  const borderCls = dm ? 'border-slate-700' : 'border-slate-200';

  const renderContent = () => {
    switch (currentPage) {
      case 'dashboard':
        return loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          </div>
        ) : (
          <div className="space-y-6">

            {/* ── Stat Cards ── */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              <Card className={cardCls}>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className={`h-12 w-12 rounded-xl flex items-center justify-center shrink-0 ${dm ? 'bg-blue-900/50' : 'bg-blue-100'}`}>
                      <GraduationCap className="h-6 w-6 text-blue-500" />
                    </div>
                    <div>
                      <p className={`text-sm font-medium ${descCls}`}>Total Students</p>
                      <p className={`text-2xl font-bold ${titleCls}`}>{stats?.totalStudents ?? 0}</p>
                      <p className={`text-xs ${mutedCls}`}>Enrolled</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className={cardCls}>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className={`h-12 w-12 rounded-xl flex items-center justify-center shrink-0 ${dm ? 'bg-emerald-900/50' : 'bg-emerald-100'}`}>
                      <Users className="h-6 w-6 text-emerald-500" />
                    </div>
                    <div>
                      <p className={`text-sm font-medium ${descCls}`}>Total Teachers</p>
                      <p className={`text-2xl font-bold ${titleCls}`}>{stats?.totalTeachers ?? 0}</p>
                      <p className={`text-xs ${mutedCls}`}>Active</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className={cardCls}>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className={`h-12 w-12 rounded-xl flex items-center justify-center shrink-0 ${dm ? 'bg-amber-900/50' : 'bg-amber-100'}`}>
                      <AlertTriangle className="h-6 w-6 text-amber-500" />
                    </div>
                    <div>
                      <p className={`text-sm font-medium ${descCls}`}>Active Incidents</p>
                      <p className={`text-2xl font-bold ${titleCls}`}>{stats?.activeIncidents ?? 0}</p>
                      {analytics?.overview.incidentTrend !== undefined && (
                        <p className={`text-xs flex items-center gap-1 ${analytics.overview.incidentTrend > 0 ? 'text-red-500' : 'text-emerald-500'}`}>
                          {analytics.overview.incidentTrend > 0
                            ? <TrendingUp className="h-3 w-3" />
                            : <TrendingDown className="h-3 w-3" />}
                          {Math.abs(analytics.overview.incidentTrend)}% vs last period
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className={cardCls}>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className={`h-12 w-12 rounded-xl flex items-center justify-center shrink-0 ${dm ? 'bg-emerald-900/50' : 'bg-emerald-100'}`}>
                      <Shield className="h-6 w-6 text-emerald-500" />
                    </div>
                    <div>
                      <p className={`text-sm font-medium ${descCls}`}>Safety Score</p>
                      <p className={`text-2xl font-bold ${titleCls}`}>{stats?.safetyScore ?? 100}%</p>
                      <p className={`text-xs ${mutedCls}`}>Based on resolved incidents</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* ── Charts Row 1 ── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

              {/* Incidents by Type */}
              <Card className={cardCls}>
                <CardHeader className="pb-3">
                  <CardTitle className={`text-base ${titleCls}`}>Incidents by Type</CardTitle>
                  <CardDescription className={descCls}>Last 30 days</CardDescription>
                </CardHeader>
                <CardContent>
                  {analytics?.incidentsByType && analytics.incidentsByType.length > 0 ? (
                    <div className="space-y-3">
                      {analytics.incidentsByType
                        .sort((a, b) => b.count - a.count)
                        .map(item => (
                          <HorizontalBar
                            key={item.type}
                            label={item.type}
                            count={item.count}
                            max={maxTypeCount}
                            colorClass={TYPE_COLORS[item.type] || 'bg-slate-400'}
                            dm={dm}
                          />
                        ))}
                    </div>
                  ) : (
                    <div className={`flex flex-col items-center justify-center h-32 ${mutedCls}`}>
                      <Activity className="h-8 w-8 mb-2" />
                      <p className="text-sm">No incidents in this period</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Incidents by Severity */}
              <Card className={cardCls}>
                <CardHeader className="pb-3">
                  <CardTitle className={`text-base ${titleCls}`}>Incidents by Severity</CardTitle>
                  <CardDescription className={descCls}>Last 30 days</CardDescription>
                </CardHeader>
                <CardContent>
                  {analytics?.incidentsBySeverity && analytics.incidentsBySeverity.length > 0 ? (
                    <div className="space-y-3">
                      {(['critical', 'high', 'medium', 'low'] as const).map(sev => {
                        const item = analytics.incidentsBySeverity.find(x => x.severity === sev);
                        if (!item) return null;
                        return (
                          <div key={sev} className="flex items-center gap-3">
                            <span className={`text-xs font-semibold w-14 capitalize ${SEVERITY_TEXT[sev]}`}>{sev}</span>
                            <div className={`flex-1 h-2.5 rounded-full overflow-hidden ${dm ? 'bg-slate-700' : 'bg-slate-100'}`}>
                              <div
                                className={`h-full rounded-full transition-all duration-500 ${SEVERITY_COLORS[sev]}`}
                                style={{ width: `${Math.round((item.count / maxSeverityCount) * 100)}%` }}
                              />
                            </div>
                            <span className={`text-xs font-semibold w-6 text-right shrink-0 ${dm ? 'text-slate-300' : 'text-slate-700'}`}>{item.count}</span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className={`flex flex-col items-center justify-center h-32 ${mutedCls}`}>
                      <Shield className="h-8 w-8 mb-2" />
                      <p className="text-sm">No incidents in this period</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* ── Charts Row 2 ── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

              {/* Monthly Trend */}
              <Card className={cardCls}>
                <CardHeader className="pb-3">
                  <CardTitle className={`text-base ${titleCls}`}>Monthly Incident Trend</CardTitle>
                  <CardDescription className={descCls}>Last 6 months</CardDescription>
                </CardHeader>
                <CardContent>
                  {analytics?.incidentsByMonth && analytics.incidentsByMonth.length > 0 ? (
                    <LineTrendChart data={analytics.incidentsByMonth.slice(-6)} dm={dm} />
                  ) : (
                    <div className={`flex flex-col items-center justify-center h-32 ${mutedCls}`}>
                      <BarChart3 className="h-8 w-8 mb-2" />
                      <p className="text-sm">No trend data available</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Students Requiring Attention (merged — shows top 6) */}
              <Card className={cardCls}>
                <CardHeader className="pb-3">
                  <CardTitle className={`text-base ${titleCls}`}>Students Requiring Attention</CardTitle>
                  <CardDescription className={descCls}>Highest incident count this period</CardDescription>
                </CardHeader>
                <CardContent>
                  {analytics?.topStudents && analytics.topStudents.length > 0 ? (
                    <div className="space-y-1">
                      {analytics.topStudents.slice(0, 6).map((s, i) => {
                        const rankBg   = i === 0 ? (dm ? '#7f1d1d' : '#fee2e2') : i === 1 ? (dm ? '#7c2d12' : '#ffedd5') : (dm ? '#1e293b' : '#f1f5f9');
                        const rankText = i === 0 ? (dm ? '#f87171' : '#dc2626') : i === 1 ? (dm ? '#fb923c' : '#ea580c') : (dm ? '#94a3b8' : '#475569');
                        const badgeBg  = s.incidentCount >= 5 ? (dm ? '#7f1d1d' : '#fee2e2') : s.incidentCount >= 3 ? (dm ? '#78350f' : '#fef3c7') : (dm ? '#1e3a5f' : '#dbeafe');
                        const badgeText = s.incidentCount >= 5 ? (dm ? '#f87171' : '#dc2626') : s.incidentCount >= 3 ? (dm ? '#fbbf24' : '#d97706') : (dm ? '#60a5fa' : '#2563eb');
                        const barColor  = s.incidentCount >= 5 ? '#ef4444' : s.incidentCount >= 3 ? '#f59e0b' : '#3b82f6';
                        return (
                          <button
                            key={i}
                            onClick={() => { setIncidentStudentFilter(s.name); navigateTo('incidents'); }}
                            className="w-full flex items-center gap-3 rounded-lg px-2 py-1.5 text-left transition-colors group"
                            style={{
                              appearance: 'none',
                              WebkitAppearance: 'none',
                              backgroundColor: 'transparent',
                              border: 'none',
                              cursor: 'pointer',
                              color: 'inherit',
                            }}
                            onMouseEnter={e => { e.currentTarget.style.backgroundColor = dm ? '#263348' : '#f8fafc'; }}
                            onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                          >
                            <div className="h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
                              style={{ backgroundColor: rankBg, color: rankText }}>
                              {i + 1}
                            </div>
                            <span className={`text-sm font-medium flex-1 truncate ${bodyTextCls} group-hover:none`}>{s.name}</span>
                            <div className="h-2 w-24 rounded-full overflow-hidden" style={{ backgroundColor: dm ? '#334155' : '#e2e8f0' }}>
                              <div className="h-full rounded-full" style={{ width: `${Math.min(100, s.incidentCount * 14)}%`, backgroundColor: barColor }} />
                            </div>
                            <span className="text-xs font-semibold px-2 py-0.5 rounded-full"
                              style={{ backgroundColor: badgeBg, color: badgeText }}>
                              {s.incidentCount}
                            </span>
                            <ChevronRight className={`h-3.5 w-3.5 shrink-0 opacity-0 group-hover:opacity-60 transition-opacity ${mutedCls}`} />
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className={`flex flex-col items-center justify-center h-32 ${mutedCls}`}>
                      <GraduationCap className="h-8 w-8 mb-2" />
                      <p className="text-sm">All students are doing well</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* ── Action Queue ── */}
            <Card className={cardCls}>
              <CardHeader className="pb-3">
                <CardTitle className={`text-base ${titleCls}`}>Action Queue</CardTitle>
                <CardDescription className={descCls}>Recommended admin actions</CardDescription>
              </CardHeader>
              <CardContent className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {([
                  {
                    title: 'Review unresolved critical alerts',
                    desc: 'Escalate unresolved alerts older than 8 hours.',
                    icon: AlertTriangle,
                    page: 'incidents' as SchoolAdminPage,
                    iconColor: dm ? 'text-red-400' : 'text-red-400',
                  },
                  {
                    title: 'Send guidance to grade-level teams',
                    desc: 'Share intervention playbooks with teachers.',
                    icon: MessageSquare,
                    page: 'messages' as SchoolAdminPage,
                    iconColor: dm ? 'text-slate-400' : 'text-slate-400',
                  },
                  {
                    title: 'Export compliance snapshot',
                    desc: "Generate this week's safety and response report.",
                    icon: FileText,
                    page: 'reports' as SchoolAdminPage,
                    iconColor: dm ? 'text-slate-400' : 'text-slate-400',
                  },
                ] as const).map((item, i) => (
                  <button
                    key={i}
                    onClick={() => navigateTo(item.page)}
                    className="group rounded-lg p-4 text-left cursor-pointer transition-all"
                    style={{
                      appearance: 'none',
                      WebkitAppearance: 'none',
                      backgroundColor: dm ? '#1e293b' : '#ffffff',
                      border: `1px solid ${dm ? '#334155' : '#e2e8f0'}`,
                      color: 'inherit',
                    }}
                    onMouseEnter={e => {
                      const el = e.currentTarget;
                      el.style.backgroundColor = dm ? '#263348' : '#f8fafc';
                      el.style.borderColor = dm ? '#475569' : '#cbd5e1';
                      el.style.boxShadow = dm ? 'none' : '0 1px 4px rgba(0,0,0,0.06)';
                    }}
                    onMouseLeave={e => {
                      const el = e.currentTarget;
                      el.style.backgroundColor = dm ? '#1e293b' : '#ffffff';
                      el.style.borderColor = dm ? '#334155' : '#e2e8f0';
                      el.style.boxShadow = 'none';
                    }}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <item.icon className={`h-4 w-4 shrink-0 mt-0.5 ${item.iconColor}`} />
                      <ArrowRight className={`h-3.5 w-3.5 shrink-0 opacity-0 group-hover:opacity-40 transition-opacity ${mutedCls}`} />
                    </div>
                    <p className={`text-sm font-semibold mt-2.5 ${bodyTextCls}`}>{item.title}</p>
                    <p className={`text-xs mt-1 leading-relaxed ${mutedCls}`}>{item.desc}</p>
                  </button>
                ))}
              </CardContent>
            </Card>

            {/* ── Recent Activity ── */}
            <Card className={cardCls}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className={`text-base ${titleCls}`}>Recent Activity</CardTitle>
                    <CardDescription className={descCls}>Latest incidents from your school</CardDescription>
                  </div>
                  <p className={`text-xs flex items-center gap-1 ${mutedCls}`}>
                    <Clock className="h-3 w-3" />
                    Updated {lastRefresh.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </CardHeader>
              <CardContent>
                <div className={`divide-y ${dividerCls}`}>
                  {recentAlerts.length > 0 ? recentAlerts.slice(0, 10).map(alert => {
                    const severityIcon = {
                      critical: 'text-red-500', high: 'text-orange-500', medium: 'text-amber-500', low: 'text-blue-400'
                    }[alert.severity] || (dm ? 'text-slate-400' : 'text-slate-600');
                    return (
                      <div key={alert.id} className="flex items-center gap-4 py-3">
                        <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${dm ? 'bg-slate-700/40' : 'bg-slate-100'}`}>
                          {alert.status === 'resolved'
                            ? <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                            : <AlertTriangle className={`h-4 w-4 ${severityIcon}`} />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium truncate ${bodyTextCls}`}>
                            {alert.alertType.replace(/_/g, ' ')} — {alert.studentName}
                          </p>
                          <p className={`text-xs ${mutedCls}`}>
                            {new Date(alert.createdAt).toLocaleString()} · {alert.status}
                          </p>
                        </div>
                        <span className={`text-xs font-semibold capitalize ${
                          alert.severity === 'critical' ? 'text-red-600' :
                          alert.severity === 'high' ? 'text-orange-500' :
                          alert.severity === 'medium' ? 'text-amber-500' :
                          'text-blue-500'
                        }`}>{alert.severity}</span>
                      </div>
                    );
                  }) : (
                    <div className={`flex items-center gap-4 py-8 justify-center ${mutedCls}`}>
                      <Shield className="h-5 w-5" />
                      <p className="text-sm">No recent incidents — your school is safe!</p>
                    </div>
                  )}
                </div>
                {recentAlerts.length > 0 && (
                  <div className={`pt-3 border-t mt-1 ${borderCls}`}>
                    <button
                      onClick={() => navigateTo('live')}
                      className="w-full flex items-center justify-center gap-1.5 text-sm py-2 rounded-lg transition-colors"
                      style={{
                        appearance: 'none',
                        WebkitAppearance: 'none',
                        backgroundColor: 'transparent',
                        border: 'none',
                        color: dm ? '#94a3b8' : '#64748b',
                        cursor: 'pointer',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.color = dm ? '#e2e8f0' : '#334155'; }}
                      onMouseLeave={e => { e.currentTarget.style.color = dm ? '#94a3b8' : '#64748b'; }}
                    >
                      {recentAlerts.length > 10 ? 'View all activity' : 'View live activity feed'}
                      <ArrowRight className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        );

      case 'adduser':
        return (
          <div className="space-y-6 max-w-6xl mx-auto">
            {/* Page header */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100">Add Users</h2>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">Invite individual users or bulk upload via CSV</p>
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <AddUserForm dm={dm} />
              <BulkUploadUsers dm={dm} />
            </div>
          </div>
        );

      case 'users':
        return <UserManagement dm={dm} />;

      case 'incidents':
        return <IncidentManagement initialSearch={incidentStudentFilter} />;

      case 'reports':
        return <AnalyticsDashboard dm={dm} />;

      case 'notifications':
        return <SchoolAdminNotifications darkMode={dm} />;

      case 'profile':
        return <SchoolAdminProfile darkMode={dm} />;

      case 'settings':
        return <SchoolSettings />;

      case 'live':
        return <LiveActivityFeed dm={dm} />;

      case 'screens':
        return <LiveScreensView dm={dm} onViewAlerts={name => { setIncidentStudentFilter(name); navigateTo('incidents'); }} />;

      case 'traffic':
        return <WebTrafficDashboard dm={dm} />;

      case 'classrooms':
        return <ClassroomView dm={dm} />;

      case 'wellness':
        return <WellnessDashboard dm={dm} />;

      case 'messages':
        return <MessageSafetyReport dm={dm} />;

      case 'ai-detection':
        return <AIDetectionPanel dm={dm} />;

      case 'invites':
        return <InviteTracker dm={dm} />;

      case 'audit':
        return <AuditLogView dm={dm} />;

      case 'workload':
        return (
          <WorkloadView
            dm={dm}
            onViewTeacher={name => { setIncidentStudentFilter(name); navigateTo('incidents'); }}
          />
        );

      case 'safety-score':
        return <SafetyScoreBreakdown dm={dm} />;

      case 'documentation':
        return <Documentation dm={dm} />;

      default:
        return null;
    }
  };

  type NavGroup = { heading: string; items: { id: SchoolAdminPage; label: string; icon: React.ElementType }[] };
  const navGroups: NavGroup[] = [
    {
      heading: 'Overview',
      items: [
        { id: 'dashboard',     label: 'Dashboard',         icon: BarChart3 },
        { id: 'live',          label: 'Live Activity',      icon: Wifi },
        { id: 'screens',       label: 'Live Screens',       icon: Monitor },
        { id: 'safety-score',  label: 'Safety Score',       icon: Shield },
      ],
    },
    {
      heading: 'Monitoring',
      items: [
        { id: 'incidents',     label: 'Incidents',         icon: AlertTriangle },
        { id: 'traffic',       label: 'Web Traffic',        icon: Globe },
        { id: 'messages',      label: 'Message Safety',     icon: MessageSquare },
        { id: 'ai-detection',  label: 'AI Detection',       icon: Brain },
      ],
    },
    {
      heading: 'School',
      items: [
        { id: 'classrooms',    label: 'Classrooms',         icon: BookOpen },
        { id: 'wellness',      label: 'Digital Wellness',   icon: Heart },
        { id: 'workload',      label: 'Teacher Workload',   icon: Layers },
      ],
    },
    {
      heading: 'Management',
      items: [
        { id: 'adduser',       label: 'Add User',           icon: UserPlus },
        { id: 'users',         label: 'Users',              icon: UserCog },
        { id: 'invites',       label: 'Invite Tracker',     icon: Mail },
        { id: 'reports',       label: 'Analytics',          icon: FileText },
        { id: 'notifications', label: 'Notifications',      icon: Bell },
        { id: 'audit',         label: 'Audit Log',          icon: ClipboardList },
      ],
    },
    {
      heading: 'Resources',
      items: [
        { id: 'documentation', label: 'Documentation', icon: BookOpen },
        { id: 'settings',      label: 'Settings',      icon: Settings },
      ],
    },
  ];

  const userInitials = (user?.name || 'SA').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  // const pageTitle = PAGE_TITLES[currentPage];

  return (
    <div className={`h-screen w-screen flex overflow-hidden ${dm ? 'bg-slate-950' : 'bg-slate-100'}`}>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* ── Sidebar ── */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50 w-64 h-full flex flex-col shrink-0
        transform transition-transform duration-200 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        bg-slate-900 border-r border-slate-800
      `}>
        {/* Logo */}
        <div className="px-5 py-5 border-b border-slate-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 min-w-24">
              <img src="/shlogo3.png" alt="ShieldEd Haven" className="h-56 w-56 mt-[-5rem] mb-[-4rem] object-cover shrink-0" />
            </div>
            <button
              className="lg:hidden text-slate-400 hover:text-slate-400 p-1 rounded transition-colors"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <p className="text-xs mt-2 text-slate-400 font-medium tracking-wide uppercase">School Admin Portal</p>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-4 overflow-y-auto no-scrollbar">
          {navGroups.map(group => (
            <div key={group.heading} className="mb-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 px-3 mb-1.5">{group.heading}</p>
              <div className="space-y-0.5">
                {group.items.map(({ id, label, icon: Icon }) => {
                  const isActive = currentPage === id;
                  return (
                    <button
                      key={id}
                      onClick={() => { setCurrentPage(id); setSidebarOpen(false); }}
                      style={{
                        appearance: 'none', WebkitAppearance: 'none',
                        width: '100%', display: 'flex', alignItems: 'center', gap: '0.75rem',
                        padding: '0.5rem 0.75rem', borderRadius: '0.5rem',
                        fontSize: '0.875rem', fontWeight: 500, border: 'none', cursor: 'pointer',
                        backgroundColor: isActive ? '#2563eb' : 'transparent',
                        color: isActive ? 'white' : '#cbd5e1',
                        transition: 'background-color 0.15s, color 0.15s',
                      }}
                      onMouseEnter={e => { if (!isActive) { const b = e.currentTarget as HTMLButtonElement; b.style.backgroundColor = '#1e293b'; b.style.color = 'white'; }}}
                      onMouseLeave={e => { if (!isActive) { const b = e.currentTarget as HTMLButtonElement; b.style.backgroundColor = 'transparent'; b.style.color = '#cbd5e1'; }}}
                    >
                      <Icon style={{ width: '1rem', height: '1rem', flexShrink: 0 }} />
                      <span>{label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* Sidebar Footer */}
        <div className="px-3 py-4 border-t border-slate-700">
          {/* User info */}
          <div className="flex items-center gap-3 px-3 mb-3">
            <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center text-slate-400 text-xs font-bold shrink-0">
              {userInitials}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-slate-200 truncate">{user?.name?.split(' ').slice(0, -1).join(' ')|| 'School Admin'}</p>
            </div>
          </div>

          {/* Profile */}
          <button
            onClick={() => { setCurrentPage('profile'); setSidebarOpen(false); }}
            style={{
              appearance: 'none', WebkitAppearance: 'none',
              width: '100%', display: 'flex', alignItems: 'center', gap: '0.75rem',
              padding: '0.5rem 0.75rem', borderRadius: '0.5rem',
              fontSize: '0.875rem', fontWeight: 500, border: 'none', cursor: 'pointer',
              backgroundColor: currentPage === 'profile' ? '#2563eb' : 'transparent',
              color: currentPage === 'profile' ? 'white' : '#94a3b8',
              transition: 'background-color 0.15s, color 0.15s',
            }}
            onMouseEnter={e => {
              if (currentPage !== 'profile') {
                const b = e.currentTarget as HTMLButtonElement;
                b.style.backgroundColor = '#1e293b';
                b.style.color = 'white';
              }
            }}
            onMouseLeave={e => {
              if (currentPage !== 'profile') {
                const b = e.currentTarget as HTMLButtonElement;
                b.style.backgroundColor = 'transparent';
                b.style.color = '#94a3b8';
              }
            }}
          >
            <UserCog style={{ width: '1rem', height: '1rem' }} />
            <span>Profile</span>
          </button>

          {/* Sign Out */}
          <button
            onClick={handleLogout}
            style={{
              appearance: 'none', WebkitAppearance: 'none',
              width: '100%', display: 'flex', alignItems: 'center', gap: '0.75rem',
              padding: '0.5rem 0.75rem', borderRadius: '0.5rem',
              fontSize: '0.875rem', fontWeight: 500, border: 'none', cursor: 'pointer',
              backgroundColor: 'transparent', color: '#94a3b8',
              transition: 'background-color 0.15s, color 0.15s',
            }}
            onMouseEnter={e => { const b = e.currentTarget as HTMLButtonElement; b.style.backgroundColor = '#1e293b'; b.style.color = '#f87171'; }}
            onMouseLeave={e => { const b = e.currentTarget as HTMLButtonElement; b.style.backgroundColor = 'transparent'; b.style.color = '#94a3b8'; }}
          >
            <LogOut style={{ width: '1rem', height: '1rem' }} />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>

      {/* ── Main Area ── */}
      <div className="flex-1 flex flex-col min-w-0 h-full overflow-hidden">

        {/* Top Header */}
        <header className={`border-b flex items-center justify-between px-4 py-8 shrink-0 gap-3 ${dm ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200'}`}>

          {/* Left: hamburger + search */}
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <button className="lg:hidden shrink-0"
              onClick={() => setSidebarOpen(true)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem', color: dm ? '#94a3b8' : '#64748b' }}>
              <Menu style={{ width: '1.25rem', height: '1.25rem' }} />
            </button>

            {/* Search bar */}
            <div className="relative w-full max-w-sm">
              <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 ${dm ? 'text-slate-500' : 'text-slate-400'}`} />
              <input
                type="text"
                placeholder="Search students…"
                className={`w-full pl-8 pr-3 py-2 text-sm rounded-3xl border outline-none transition
                  ${dm
                    ? 'bg-slate-800 border-slate-700 text-slate-200 placeholder-slate-500 focus:border-slate-500 focus:ring-1 focus:ring-slate-600'
                    : 'bg-slate-50 border-slate-200 text-slate-700 placeholder-slate-400 focus:border-blue-300 focus:ring-2 focus:ring-blue-100 focus:bg-white'
                  }`}
              />
            </div>
          </div>

          {/* Right: school info + date/time + controls */}
          <div className="flex items-center gap-3 shrink-0">

            {/* School name + plan */}
            {schoolName && (
              <div className="hidden md:flex flex-col items-end leading-tight">
                <span className={`text-sm font-semibold truncate max-w-[180px] ${dm ? 'text-slate-200' : 'text-slate-700'}`}>
                  {schoolName}
                </span>
                <span className={`text-xs font-medium capitalize ${
                  schoolPlan === 'enterprise' ? (dm ? 'text-violet-400' : 'text-violet-600') :
                  schoolPlan === 'lite' ? (dm ? 'text-blue-400' : 'text-blue-600') :
                  (dm ? 'text-slate-400' : 'text-slate-500')
                }`}>
                  {schoolPlan || 'Free'} Plan
                </span>
              </div>
            )}

            {schoolName && <div className={`hidden md:block w-px h-6 ${dm ? 'bg-slate-700' : 'bg-slate-200'}`} />}

            {/* Date & time */}
            <div className="hidden sm:flex flex-col items-end leading-tight">
              <span className={`text-sm font-semibold tabular-nums ${dm ? 'text-slate-200' : 'text-slate-700'}`}>
                {now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
              <span className={`text-xs ${dm ? 'text-slate-500' : 'text-slate-400'}`}>
                {now.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
              </span>
            </div>

            <div className={`hidden sm:block w-px h-6 ${dm ? 'bg-slate-700' : 'bg-slate-200'}`} />

            {/* Dark mode toggle */}
            <button
              onClick={() => setDarkMode(d => !d)}
              title={dm ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
              className={`w-8 h-8 flex items-center justify-center rounded-lg transition ${dm ? 'text-slate-300 hover:bg-slate-700 hover:text-slate-400' : 'text-slate-700 hover:bg-slate-100'}`}
              style={{ background: 'none', border: 'none', cursor: 'pointer', outline: 'none' }}
            >
              {dm
                ? <span style={{ color: '#e2e8f0' }}><Sun size={24} /></span>
                : <span style={{ color: '#000000' }}><Moon size={24} /></span>
              }
            </button>

            {/* Notifications bell */}
            <button
              onClick={() => setCurrentPage('notifications')}
              title="Notifications"
              className={`w-8 h-8 flex items-center justify-center rounded-lg transition ${dm ? 'text-slate-300 hover:bg-slate-700 hover:text-slate-400' : 'text-slate-700 hover:bg-slate-100'}`}
              style={{ background: 'none', border: 'none', cursor: 'pointer', outline: 'none' }}
            >
              <span style={{ color: dm ? '#e2e8f0' : '#000000' }}><Bell size={24} /></span>
            </button>

            {/* Avatar */}
            <button
              onClick={() => setCurrentPage('profile')}
              style={{ background: 'none', border: 'none', cursor: 'pointer', outline: 'none', padding: 0 }}
            >
              <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center text-slate-400 text-xs font-bold shrink-0">
                {userInitials}
              </div>
            </button>
          </div>
        </header>

        {/* Page Content */}
        <main className={`flex-1 p-6 overflow-y-auto no-scrollbar ${dm ? 'bg-slate-950' : ''}`}>
          {renderContent()}
        </main>
      </div>
    </div>
  );
}

export default SchoolAdminDashboard;
